import { TLabelCase } from '@/features/cases/label-case/types/label-case.type';
import { defaultFilterCase, IFilterCase } from '../stores/cases.store';

import { TUserStatesStore } from '@/features/user/stores/user.store';
import { TTabCaseQuery } from '../hooks/use-tabcase-qs';
import { TVariantCaseQuery } from '../hooks/use-variantcase-qs';
import { TCase } from '@/features/cases/case/api/types/case.type';

export const calculateTotalFilterCases = (filterCase?: IFilterCase): number => {
  let total = 0;

  // Count each array length
  total += filterCase?.labelCases?.length || 0;
  total += filterCase?.channels?.length || 0;
  total += filterCase?.agents?.length || 0;
  total += filterCase?.teams?.length || 0;

  // Add 1 if there's a search term
  if (filterCase?.searchCustomerOrGroupName?.trim()) {
    total += 1;
  }

  return total;
};

export const countSelectedCases = (listCases: TCase[] | null, selectedCases: TCase[] | null) => {
  if (!listCases || !selectedCases || selectedCases.length === 0) return 0;

  // PERFORMANCE OPTIMIZATION: Convert selected cases to Set for O(1) lookup
  // This changes the algorithm from O(n²) to O(n)
  const selectedIds = new Set(selectedCases.map((c) => c.conversationId));

  let count = 0;
  for (const caseItem of listCases) {
    if (selectedIds.has(caseItem.conversationId)) {
      count++;
    }
  }
  return count;
};

export const filterCases = (list: TCase[], filter: IFilterCase) => {
  if (!list || list.length === 0) return [];

  // PERFORMANCE OPTIMIZATION: Pre-compute filter sets for O(1) lookups
  const labelIds = new Set(filter.labelCases?.map((l) => l.labelCaseId) || []);
  const channelIds = new Set(filter.channels?.map((c) => c.organizationChannelId) || []);
  const agentIds = new Set(filter.agents?.map((a) => a.userId) || []);
  const teamIds = new Set(filter.teams?.map((t) => t.teamId) || []);
  const searchTerm = filter.searchCustomerOrGroupName?.toLowerCase() || '';

  const filteredList = list.filter((item) => {
    // Early returns for better performance
    if (labelIds.size > 0 && !item?.labelCases?.some((label: TLabelCase) => labelIds.has(label.labelCaseId))) {
      return false;
    }
    if (channelIds.size > 0 && !channelIds.has(item.organizationChannelId)) {
      return false;
    }
    if (agentIds.size > 0 && !agentIds.has(item.userId)) {
      return false;
    }
    if (teamIds.size > 0 && !teamIds.has(item.teamId)) {
      return false;
    }
    if (searchTerm) {
      const matchesCustomer = item.customerName?.toLowerCase().includes(searchTerm);
      const matchesGroupName = item.groupName?.toLowerCase().includes(searchTerm);
      if (!matchesCustomer && !matchesGroupName) {
        return false;
      }
    }

    return true;
  });

  return filteredList;
};

export const sortCases = (list?: TCase[]) => {
  if (!list || list.length === 0) return [];

  // PERFORMANCE OPTIMIZATION: Create a copy before sorting to avoid mutating original array
  return [...list].sort((a, b) => {
    // Direct comparison without creating Date objects for better performance
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};

export const filterAndSortCases = (list?: TCase[], filter?: IFilterCase) => {
  if (!list || list.length === 0) {
    return [];
  }
  const filtered = filterCases(list, filter || defaultFilterCase);
  const sorted = sortCases(filtered);
  return sorted;
};

export const filterByTabCaseAndRole = (list?: TCase[], tabCase?: TTabCaseQuery, user?: TUserStatesStore) => {
  if (!list || list.length === 0 || !user?.role) {
    return [];
  }

  const roleName = user.role.name;
  const userId = user.userId;

  // PERFORMANCE OPTIMIZATION: Early returns and single filter operations
  if (roleName === 'agent') {
    return list.filter((item) => item.userId === userId);
  }

  if (roleName === 'supervisor') {
    if (tabCase === 'myCase') {
      return list.filter((item) => item.userId === userId);
    } else if (tabCase === 'teamCase') {
      return list.filter((item) => item.userId !== userId);
    }
  }

  // Admin and other roles see all cases
  return list;
};

export const countCase = (
  list: TCase[],
  user: TUserStatesStore,
  filter: IFilterCase = defaultFilterCase,
  variantCase: TVariantCaseQuery
) => {
  if (!list || list.length === 0 || !user?.role) {
    return { myCase: 0, teamCase: 0, allCase: 0 };
  }

  // PERFORMANCE OPTIMIZATION: Apply filters first, then count
  const filteredList = filterCases(list, filter);

  if (filteredList.length === 0) {
    return { myCase: 0, teamCase: 0, allCase: 0 };
  }

  const allCase = filteredList.length;

  if (variantCase !== 'activeCase') {
    return { myCase: 0, teamCase: 0, allCase };
  }

  const roleName = user.role.name;
  const userId = user.userId;

  // PERFORMANCE OPTIMIZATION: Single pass through the filtered list for all counts
  if (roleName === 'agent') {
    const myCase = filteredList.filter((item) => item.userId === userId).length;
    return { myCase, teamCase: 0, allCase: 0 };
  }

  if (roleName === 'supervisor') {
    // PERFORMANCE OPTIMIZATION: Calculate both counts in one pass
    let myCase = 0;
    let teamCase = 0;

    for (const item of filteredList) {
      if (item.userId === userId) {
        myCase++;
      } else {
        teamCase++;
      }
    }

    return { myCase, teamCase, allCase: 0 };
  }

  if (roleName === 'admin') {
    return { myCase: 0, teamCase: 0, allCase };
  }

  return { myCase: 0, teamCase: 0, allCase: 0 };
};
